import type { FcfEntitlementBar } from '@/features/decisions/decisions.api.types';

// Operating Cash Flow lines for one scenario, given the lost-sales haircut
// (0..1) applied to backlog-fulfilled revenue.
//
//   Cash In        = revenue_demand + (1 − lostSales%) · revenue_backlog
//   COGS           = model_cogs   (cost_per_unit × units fulfilled)
//   EBIT           = Cash In − COGS
//   Change in NWC  = production cost − COGS   (Total Production Cost − COGS)
//   Operating CF   = EBIT − Change in NWC = Cash In − production cost
//
// COGS cancels in the OCF total, so OCF and Cash In resolve from fields the
// entitlement endpoint already serves; EBIT / COGS / ΔNWC need `model_cogs`
// (backend #9) and render null until it lands.
export interface OcfLines {
  cashIn: number | null;
  productionCost: number | null;
  cogs: number | null;
  ebit: number | null;
  changeInNwc: number | null;
  ocf: number | null;
}

const sub = (a: number | null, b: number | null): number | null => (a == null || b == null ? null : a - b);

export function computeOcf(bar: FcfEntitlementBar | undefined, lostSalesPct: number): OcfLines {
  if (!bar) return { cashIn: null, productionCost: null, cogs: null, ebit: null, changeInNwc: null, ocf: null };

  const revDemand = bar.model_revenue_demand_fulfilled;
  const revBacklog = bar.model_revenue_backlog_fulfilled;
  const prodCost = bar.model_order_placed_total_cost;
  const cogs = bar.model_cogs ?? null;

  const cashIn =
    revDemand == null && revBacklog == null ? null : (revDemand ?? 0) + (1 - lostSalesPct) * (revBacklog ?? 0);
  const ebit = sub(cashIn, cogs);
  const changeInNwc = sub(prodCost, cogs);
  // OCF = Cash In − production cost (COGS cancels) → resolves before model_cogs.
  const ocf = sub(cashIn, prodCost);

  return { cashIn, productionCost: prodCost, cogs, ebit, changeInNwc, ocf };
}

// Optimized − Incumbent for each OCF line, under the same lost-sales haircut.
export type OcfDeltas = OcfLines;

export function computeOcfDeltas(
  ref: FcfEntitlementBar | undefined,
  cmp: FcfEntitlementBar | undefined,
  lostSalesPct: number,
): OcfDeltas {
  const r = computeOcf(ref, lostSalesPct);
  const c = computeOcf(cmp, lostSalesPct);
  return {
    cashIn: sub(c.cashIn, r.cashIn),
    productionCost: sub(c.productionCost, r.productionCost),
    cogs: sub(c.cogs, r.cogs),
    ebit: sub(c.ebit, r.ebit),
    changeInNwc: sub(c.changeInNwc, r.changeInNwc),
    ocf: sub(c.ocf, r.ocf),
  };
}
